import { useEffect, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel'
import PaidIcon from '@mui/icons-material/Paid';
import LockIcon from '@mui/icons-material/Lock';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { EtiquetasDaComanda } from './etiquetas-comanda';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { RootState } from '../../../store';
import {
  useAdminAtualizarConfiguracaoMutation,
  useAdminObterConfiguracaoQuery,
  useAdminObterOnboardingQuery,
  useAdminObterEnderecoEmpresaQuery,
  useAdminListarLocaisImpressaoQuery,
  useAdminMeQuery,
  useAdminListarConfigPontosAcessoQuery,
  useAdminSalvarConfigPontoAcessoMutation,
  useAdminDuplicarConfigPontoAcessoMutation,
  type ConfigPontoAcessoOverride,
} from '../../../api';
import { DialogReplicarConfigTotem } from './dialog-replicar-config-totem';
import { FormaRetiradaCheckbox } from '../../../components/forms/forma-retirada-checkbox';
import { TimezoneSelect } from '../../../components/forms/timezone-select';
import { TIMEZONE_PADRAO } from '../../../lib/timezones-brasil';
import { DESTINO_CHAMADO_KDS, comValorSalvo, opcoesImpressora, textoAjudaDestino } from '../../../lib/destino-chamado';
import { HeaderTela } from '../../../components/admin/header-tela';
import { MultiSelectMunicipios } from './multi-select-municipios';
import { AvisoPinDispositivo } from '../../../components/admin/aviso-pin-dispositivo';
import { InfoModo } from '../../../components/admin/painel-info';
import { PreviewVisualCardapio } from '../../../components/admin/preview-visual-cardapio';
import { UploadImagensTotem } from '../../../components/admin/upload-imagens-totem';
import { EditorBanners, type PedidoAbrirBanner } from '../../../components/admin/editor-banners'
import { problemaNosBanners, type BannerRascunho } from '../../../lib/banners'
import {
  BotaoGerenciarPontosDeAcesso,
  PontosDeAcessoCard,
} from '../../../components/admin/pontos-de-acesso-card';
import { ModuloNaoContratado } from '../../../components/admin/modulo-nao-contratado';
import { EventosRastreados } from '../../../components/admin/eventos-rastreados';
import { MODOS_CARDAPIO, type AcessoModos, type ModoCardapio } from '../../../lib/modos-cardapio';
import { abrirOnboarding } from '../../../lib/onboarding-eventos';
import SecaoCheckoutKiosk from './secao-checkout-kiosk';
import SecaoTotemPedidos from './secao-totem-pedidos';
import { SecaoCashback } from './secao-cashback'
import { AvisoCardapioExcluido, SecaoExcluirCardapio } from './exclusao-cardapio';
import { SecaoAvisosCliente } from './secao-avisos-cliente'

type Form = Record<string, unknown>;

/**
 * Índice de cada aba. Cada `<Tab>` declara seu próprio `value` daqui, então a
 * ordem de EXIBIÇÃO é a ordem do JSX — reordenar as abas não renumera nada nem
 * exige mexer nos blocos de conteúdo (`aba === ABA.x`).
 */
const ABA = {
  gerais: 0,
  delivery: 1,
  comandaMesa: 2,
  vitrine: 3,
  tablet: 4,
  totem: 5,
  totemCheckout: 6,
} as const;

/**
 * Gateia o conteúdo de uma aba de modo: mostra o conteúdo se a empresa
 * contratou o modo; senão, o aviso "módulo não contratado" com botão solicitar.
 * Enquanto `modos` não carregou (undefined), mostra o conteúdo (otimista) para
 * não piscar o aviso — o `me` já costuma vir do cache do layout.
 */
export function GateModo({
  modo,
  modos,
  children,
}: {
  modo: ModoCardapio;
  modos: AcessoModos | undefined;
  children: ReactNode;
}): JSX.Element {
  if (modos && !modos[modo]) {
    return <ModuloNaoContratado nomeModo={MODOS_CARDAPIO[modo].nome} role={MODOS_CARDAPIO[modo].role} />;
  }
  return <>{children}</>;
}

export function AdminConfiguracaoPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const { data, isLoading } = useAdminObterConfiguracaoQuery(idConfig);
  // Modos contratados pela empresa — gateiam cada aba de modo (delivery, etc.).
  const { data: me } = useAdminMeQuery();
  const modos = me?.modos;
  const { data: enderecoEmpresa } = useAdminObterEnderecoEmpresaQuery(idConfig);
  const { data: onboarding } = useAdminObterOnboardingQuery(idConfig, { refetchOnMountOrArgChange: true });
  const [atualizar, statusAtualizar] = useAdminAtualizarConfiguracaoMutation();
  const [salvarOverridePonto] = useAdminSalvarConfigPontoAcessoMutation();
  const [duplicarConfigPonto] = useAdminDuplicarConfigPontoAcessoMutation();
  const [form, setForm] = useState<Form>({});
  const [salvo, setSalvo] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [confirmandoReplica, setConfirmandoReplica] = useState(false);
  const [replicados, setReplicados] = useState<number | null>(null);
  const [overridesPontos, setOverridesPontos] = useState<Record<number, ConfigPontoAcessoOverride>>({});
  const [idPontoCheckout, setIdPontoCheckout] = useState<number | null>(null);
  const [idPontoTotem, setIdPontoTotem] = useState<number | null>(null);
  const [aba, setAba] = useState<number>(ABA.gerais);
  const [bannerParaAbrir, setBannerParaAbrir] = useState<PedidoAbrirBanner | null>(null)
  const { data: dadosPontosCheckout } = useAdminListarConfigPontosAcessoQuery(
    { idConfig, tipo: 'TOTEM_CHECKOUT' },
    { skip: aba !== ABA.totemCheckout },
  );
  const pontosCheckout = dadosPontosCheckout?.pontos ?? [];
  const { data: dadosPontosTotem } = useAdminListarConfigPontosAcessoQuery(
    { idConfig, tipo: 'TOTEM' },
    { skip: aba !== ABA.totem },
  );
  const pontosTotem = dadosPontosTotem?.pontos ?? [];
  // "Salvar e replicar" parte do totem escolhido na aba aberta e só alcança os do mesmo tipo.
  const replica =
    aba === ABA.totemCheckout
      ? { idOrigem: idPontoCheckout, pontos: pontosCheckout }
      : aba === ABA.totem
        ? { idOrigem: idPontoTotem, pontos: pontosTotem }
        : null;
  // Impressoras: só as abas Comanda/Mesa e Tablet têm combo — as duas apontam a
  // mesma impressora do chamado de garçom. Totem e Totem Checkout escolhem a
  // deles no próprio aparelho. A lista é dinâmica (muda na loja), então rebusca
  // ao abrir — sem servir cache velho.
  const { data: locaisImpressao } = useAdminListarLocaisImpressaoQuery(idConfig, {
    skip: aba !== ABA.comandaMesa && aba !== ABA.tablet,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!data) return;
    // Os sliders de tempo exibem um padrão quando o campo vem null. Esse padrão
    // é só visual: sem isso, salvar sem arrastar o slider persistiria null e a
    // métrica sumiria do topo da loja. Refletimos o padrão no form pra que "o
    // que aparece é o que salva".
    setForm({
      ...data,
      tempoMinPreparo: data.tempoMinPreparo ?? 20,
      tempoMaxPreparo: data.tempoMaxPreparo ?? 40,
      tempoRetirada: data.tempoRetirada ?? 10,
      tempoEntrega: data.tempoEntrega ?? 45,
      freteGratisAPartirDe: data.freteGratisAPartirDe == null ? null : Number(data.freteGratisAPartirDe).toFixed(2),
    });
  }, [data]);

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  function setCampo(campo: string, valor: unknown): void {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function setOverridePonto(idPonto: number, override: ConfigPontoAcessoOverride | null): void {
    setOverridesPontos((m) => {
      if (override === null) {
        const { [idPonto]: _removido, ...resto } = m;
        return resto;
      }
      return { ...m, [idPonto]: override };
    });
  }

  async function salvar(idsDestinoReplica?: number[]): Promise<void> {
    setErroSalvar(null);
    // O servidor recusaria com um 400 genérico; aqui o lojista fica sabendo qual banner corrigir.
    const problemaBanners = problemaNosBanners(form.banners as BannerRascunho[] | undefined)
    if (problemaBanners) {
      setErroSalvar(problemaBanners.mensagem)
      setAba(ABA.gerais)
      setBannerParaAbrir((anterior) => ({ idBanner: problemaBanners.idBanner, vez: (anterior?.vez ?? 0) + 1 }))
      return
    }
    // `ativo` só muda por excluir/restaurar; o form reenvia o GET inteiro.
    const { ativo: _ativo, ...semAtivo } = form;
    const opcoes = semAtivo.tabletChamarGarcomOpcoes;
    const dados = Array.isArray(opcoes)
      ? { ...semAtivo, tabletChamarGarcomOpcoes: opcoes.map((s) => String(s).trim()).filter(Boolean).slice(0, 8) }
      : semAtivo;

    try {
      await atualizar({ id: idConfig, dados }).unwrap();

      for (const [id, configuracao] of Object.entries(overridesPontos)) {
        await salvarOverridePonto({ id: Number(id), configuracao }).unwrap();
      }

      if (idsDestinoReplica?.length && replica?.idOrigem != null) {
        const { atualizados } = await duplicarConfigPonto({
          idOrigem: replica.idOrigem,
          idsDestino: idsDestinoReplica,
        }).unwrap();
        setReplicados(atualizados);
      }

      setOverridesPontos({});
      setSalvo(true);
    } catch {
      setErroSalvar('Falha ao salvar. Confira os campos e tente novamente.');
    }
  }

  const localImpressaoSalvo = (form.tabletLocalImpressao as string) ?? '';
  const opcoesLocalImpressao = opcoesImpressora(locaisImpressao);
  const todasOpcoesLocal = comValorSalvo(localImpressaoSalvo, opcoesLocalImpressao);
  // Antes de o formulário receber o GET (form.id ainda vazio) não há o que comparar.
  const bannersNaoSalvos = form.id === data.id && JSON.stringify(form.banners ?? []) !== JSON.stringify(data.banners ?? [])
  const emailAcompanhamentoLigado = (form.emailAcompanhamentoPedido as boolean | undefined) ?? true

  return (
    <>
      <HeaderTela
        titulo="Configurações do cardápio"
        descricao="Ajuste como sua loja aparece e funciona para os clientes."
        acoes={!data.ativo ? <Chip label="Excluída" color="error" size="small" /> : undefined}
      />

      <AvisoCardapioExcluido key={data.id} loja={data} />

      {data.ativo && onboarding && onboarding.percentual < 100 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => abrirOnboarding()}>
              Ver primeiros passos
            </Button>
          }
        >
          Sua loja está <strong>{onboarding.percentual}%</strong> configurada. Conclua os primeiros passos para
          começar a vender.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={aba}
          onChange={(_e, v) => {
            setAba(v as number)
            // O pedido de abrir um banner vale para o "Salvar" barrado; trocar de aba não pode reabri-lo depois.
            setBannerParaAbrir(null)
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab value={ABA.gerais} icon={<SettingsIcon />} iconPosition="start" label="Gerais" />
          <Tab value={ABA.delivery} icon={<DeliveryDiningIcon />} iconPosition="start" label="Delivery" />
          <Tab
            value={ABA.comandaMesa}
            icon={<TableRestaurantIcon />}
            iconPosition="start"
            label="Comanda/Mesa"
          />
          <Tab value={ABA.vitrine} icon={<StorefrontIcon />} iconPosition="start" label="Vitrine" />
          <Tab
            value={ABA.tablet}
            icon={<TabletAndroidIcon sx={{ transform: 'rotate(90deg)' }} />}
            iconPosition="start"
            label="Tablet (na mesa)"
          />
          <Tab value={ABA.totem} icon={<SlideshowIcon />} iconPosition="start" label="Totem" />
          <Tab
            value={ABA.totemCheckout}
            icon={<PointOfSaleIcon />}
            iconPosition="start"
            label="Totem Checkout"
          />
        </Tabs>
      </Paper>

      <Stack spacing={3}>
        {/* ===================== ABA: GERAIS ===================== */}
        {aba === ABA.gerais && (
          <>
            {/* "Identidade" e "Visual" eram dois quadros e o nome da loja aparecia
                nos dois — campo num, texto na prévia do outro. Agora é um só: o
                nome se edita na própria prévia (como a capa e o logo já faziam). */}
            <Secao
              icone={<StorefrontIcon />}
              titulo="Identidade"
              descricao="Nome, logo e capa — é assim que o cliente vê sua loja no app."
            >
              <PreviewVisualCardapio
                urlCover={form.urlCover as string | null}
                urlLogo={form.urlLogo as string | null}
                nome={(form.nome as string) ?? ''}
                onChangeCover={(url) => setCampo('urlCover', url)}
                onChangeLogo={(url) => setCampo('urlLogo', url)}
                onChangeNome={(nome) => setCampo('nome', nome)}
              />
            </Secao>

            <Secao
              icone={<ViewCarouselIcon />}
              titulo="Banners do cardápio"
              descricao="Até 8 imagens que passam no topo do cardápio do celular e do computador (delivery, mesa e vitrine). No totem e no tablet, não aparecem. Use imagens na horizontal, proporção 2:1 (ex.: 1200×600 px): imagem quadrada ou em pé fica cortada em cima e embaixo. O banner some sozinho quando está fora do período ou quando leva a algo que não está no cardápio agora."
            >
              <EditorBanners
                idConfig={idConfig}
                valor={form.banners as BannerRascunho[] | undefined}
                onChange={(v) => setCampo('banners', v)}
                abrirBanner={bannerParaAbrir}
                naoSalvo={bannersNaoSalvos}
              />
            </Secao>

            <Secao
              icone={<LocationOnIcon />}
              titulo="Endereço da loja"
              descricao="Endereço de origem da loja — vem do cadastro da empresa no Sischef."
            >
              <Paper variant="outlined" sx={{ p: 2 }}>
                {enderecoEmpresa ? (
                  enderecoEmpresa.endereco ? (
                    <>
                      <Typography>
                        {enderecoEmpresa.endereco}
                        {enderecoEmpresa.numero ? `, ${enderecoEmpresa.numero}` : ''}
                        {enderecoEmpresa.complemento ? ` — ${enderecoEmpresa.complemento}` : ''}
                      </Typography>
                      <Typography color="text.secondary">
                        {[enderecoEmpresa.bairro, [enderecoEmpresa.municipio, enderecoEmpresa.uf].filter(Boolean).join('/')]
                          .filter(Boolean)
                          .join(' — ')}
                        {enderecoEmpresa.cep ? ` · CEP ${enderecoEmpresa.cep}` : ''}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">A empresa ainda não tem endereço cadastrado no Sischef.</Typography>
                  )
                ) : (
                  <CircularProgress size={18} />
                )}
                <Alert
                  severity="info"
                  icon={<HelpOutlineIcon fontSize="inherit" />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  <AlertTitle>Como alterar o endereço?</AlertTitle>
                  Ele vem do <strong>cadastro da empresa</strong>
                  {enderecoEmpresa?.nomeUnidade ? <> (<strong>{enderecoEmpresa.nomeUnidade}</strong>)</> : ''} e não é
                  editado por aqui. Para mudar, edite o cadastro da empresa no <strong>Sischef Retaguarda</strong>.
                </Alert>
              </Paper>
            </Secao>

            <Secao
              icone={<ContactPhoneIcon />}
              titulo="Contato"
              descricao="Canais que o cliente usa para falar com a loja — exibidos quando um pedido não é confirmado a tempo."
            >
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="WhatsApp"
                    fullWidth
                    value={(form.whatsapp as string) ?? ''}
                    onChange={(e) => setCampo('whatsapp', e.target.value || null)}
                    placeholder="(11) 99999-9999"
                    helperText="Com DDD. Vira botão 'Falar no WhatsApp' (opcional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsAppIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Instagram"
                    fullWidth
                    value={(form.instagram as string) ?? ''}
                    onChange={(e) => setCampo('instagram', e.target.value || null)}
                    placeholder="@sualoja"
                    helperText="@usuário ou link do perfil (opcional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InstagramIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Secao>

            <Secao icone={<RoomServiceIcon />} titulo="Operação" descricao="Formas de retirada e fuso horário.">
              <Stack spacing={4}>
                <FormaRetiradaCheckbox
                  valor={(form.formaRetirada as string) ?? ''}
                  onChange={(v) => setCampo('formaRetirada', v)}
                />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimezoneSelect
                      valor={(form.timezone as string) ?? TIMEZONE_PADRAO}
                      onChange={(v) => setCampo('timezone', v)}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Secao>

            <SecaoCashback
              idConfig={idConfig}
              temModulo={me?.temModuloCashback}
              ativo={form.cashbackAtivo as boolean | undefined}
              aoMudar={(ligado) => setCampo('cashbackAtivo', ligado)}
              usoAtivo={form.cashbackUsoAtivo as boolean | undefined}
              aoMudarUso={(ligado) => setCampo('cashbackUsoAtivo', ligado)}
            />

            <Secao
              icone={<ScheduleIcon />}
              titulo="Tempos de preparo e retirada"
              descricao="O que o cliente vê na hora de fazer o pedido."
            >
              <SliderTempoRange
                rotulo="Tempo de preparo"
                ajuda="Faixa estimada que o cliente vê antes do pedido sair da cozinha."
                min={0}
                max={120}
                step={5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 30, label: '30' },
                  { value: 60, label: '60' },
                  { value: 90, label: '90' },
                  { value: 120, label: '120' },
                ]}
                valorMin={(form.tempoMinPreparo as number) ?? 20}
                valorMax={(form.tempoMaxPreparo as number) ?? 40}
                onChange={(min, max) => {
                  setCampo('tempoMinPreparo', min);
                  setCampo('tempoMaxPreparo', max);
                }}
              />
              <Divider sx={{ my: 4 }} />
              <SliderTempo
                rotulo="Tempo de retirada"
                ajuda="Em quanto tempo o pedido fica pronto para buscar no balcão. Aparece no topo da loja."
                min={0}
                max={240}
                step={5}
                marks={[
                  { value: 0, label: '0' },
                  { value: 60, label: '60' },
                  { value: 120, label: '120' },
                  { value: 180, label: '180' },
                  { value: 240, label: '240' },
                ]}
                valor={(form.tempoRetirada as number) ?? 10}
                onChange={(v) => setCampo('tempoRetirada', v)}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, color: 'text.secondary' }}>
                <DeliveryDiningIcon fontSize="small" />
                <Typography variant="body2">
                  O <strong>tempo de entrega</strong> do delivery é configurado na aba <strong>Delivery</strong>.
                </Typography>
              </Box>
            </Secao>

            <Secao
              icone={<LockIcon />}
              titulo="PIN do dispositivo"
              descricao="Libera a configuração feita no próprio aparelho. É o MESMO PIN para todos os aparelhos da loja: Totem, Tablet (na mesa) e Totem Checkout."
            >
              <Grid container spacing={3} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                  <TextField
                    label="PIN"
                    fullWidth
                    value={(form.pinTotem as string) ?? ''}
                    onChange={(e) => setCampo('pinTotem', e.target.value.replace(/\D/g, '').slice(0, 8) || null)}
                    inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                    helperText="Somente números, até 8 dígitos."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 7, md: 8 }}>
                  <Stack spacing={1} sx={{ color: 'text.secondary', mt: { sm: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SlideshowIcon fontSize="small" />
                      <Typography variant="body2">
                        No <strong>totem</strong>: local de impressão do TEF.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TabletAndroidIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                      <Typography variant="body2">
                        No <strong>tablet</strong>: número da mesa do aparelho.
                      </Typography>
                    </Box>
                    <Typography variant="caption">Em branco, essa configuração fica bloqueada.</Typography>
                  </Stack>
                </Grid>

                <Grid size={12}>
                  <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <AlertTitle>Como abrir essa configuração no aparelho</AlertTitle>
                    No próprio totem ou tablet, toque no ícone <strong>ⓘ</strong> discreto no{' '}
                    <strong>canto inferior esquerdo</strong> da tela → <strong>Sobre o Sischef Cardápio</strong>{' '}
                    → <strong>Configurações do aparelho</strong>. Aí ele pede este PIN. O caminho é o mesmo
                    em todos os modos (totem, tablet e totem de checkout).
                  </Alert>
                </Grid>
              </Grid>
            </Secao>

            <Secao
              icone={<IntegrationInstructionsIcon />}
              titulo="Integrações"
              descricao="Tracking e notificações externas."
            >
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Pixel da Meta (Facebook)"
                    fullWidth
                    value={(form.pixelFacebookId as string) ?? ''}
                    onChange={(e) => setCampo('pixelFacebookId', e.target.value || null)}
                    placeholder="123456789012345"
                    helperText="ID do Pixel da Meta (Facebook) para rastreio (opcional)"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Google Analytics da sua loja"
                    fullWidth
                    value={(form.googleAnalyticsId as string) ?? ''}
                    onChange={(e) => setCampo('googleAnalyticsId', e.target.value || null)}
                    placeholder="G-XXXXXXXXXX"
                    helperText="Measurement ID da SUA conta do Google Analytics (opcional)"
                  />
                </Grid>
              </Grid>
              <EventosRastreados />
            </Secao>

            {me && data.ativo && <SecaoExcluirCardapio loja={data} podeExcluir={me.podeExcluirLoja} />}
          </>
        )}

        {/* ===================== ABA: DELIVERY ===================== */}
        {aba === ABA.delivery && (
          <GateModo modo="delivery" modos={modos}>
            <InfoModo
              itens={[
                <>
                  Os pedidos caem no <strong>PDV Delivery</strong> do Sischef, junto com os das demais
                  plataformas de entrega.
                </>,
                <>
                  O cliente escolhe entre <strong>receber em casa</strong> e <strong>retirar na loja</strong>;
                  na retirada o pedido chega marcado como retirada, sem taxa de entrega.
                </>,
                <>
                  É o único modo com <strong>cliente identificado</strong>: ele entra com e-mail, e o Sischef{' '}
                  {emailAcompanhamentoLigado
                    ? 'devolve o andamento do pedido por e-mail e em tempo real na tela dele.'
                    : 'mostra o andamento do pedido em tempo real na tela dele.'}
                </>,
              ]}
            />

            <Secao
              icone={<QrCode2Icon />}
              titulo="Pontos de acesso"
              descricao="Links de delivery e vitrine desta loja. Clique para abrir ou copie para divulgar."
              acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['DELIVERY']} />}
            >
              <PontosDeAcessoCard idConfig={idConfig} tipos={['DELIVERY']} />
            </Secao>

            <Secao
              icone={<LocationOnIcon />}
              titulo="Área de entrega"
              descricao="Municípios atendidos pelo delivery."
            >
              <MultiSelectMunicipios idConfig={idConfig} />
            </Secao>

            <Secao icone={<DeliveryDiningIcon />} titulo="Delivery" descricao="Pedido mínimo e tempo de entrega.">
              <Stack spacing={4}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CampoValorReais
                      label="Pedido mínimo (delivery)"
                      valor={form.valorMinimoPedido as number | undefined}
                      onChange={(v) => setCampo('valorMinimoPedido', v)}
                      ajuda="Deixe 0 para não exigir valor mínimo."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CampoValorReais
                      label="Taxa de conveniência"
                      valor={form.taxaConveniencia as number | undefined}
                      onChange={(v) => setCampo('taxaConveniencia', v)}
                      ajuda="Valor fixo somado ao total, exibido só na confirmação do pedido. Deixe 0 para não cobrar."
                    />
                  </Grid>
                </Grid>
                <SliderTempo
                  rotulo="Tempo de entrega"
                  ajuda="Tempo estimado de entrega no delivery, exibido no topo da loja."
                  min={0}
                  max={120}
                  step={5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 30, label: '30' },
                    { value: 60, label: '60' },
                    { value: 90, label: '90' },
                    { value: 120, label: '120' },
                  ]}
                  valor={(form.tempoEntrega as number) ?? 45}
                  onChange={(v) => setCampo('tempoEntrega', v)}
                />
                <TextField
                  select
                  label="Agendamento de pedidos"
                  fullWidth
                  value={(form.agendamentoDelivery as string) ?? 'SEMPRE'}
                  onChange={(e) => setCampo('agendamentoDelivery', e.target.value)}
                  helperText="Permite ao cliente agendar o pedido para um horário futuro. Em 'Só quando fechada', o agendamento aparece apenas fora do horário de funcionamento."
                >
                  <MenuItem value="SEMPRE">Sempre disponível</MenuItem>
                  <MenuItem value="SO_FECHADA">Só quando a loja está fechada</MenuItem>
                  <MenuItem value="DESATIVADO">Desativado</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={(form.permitirPedidoSemLogin as boolean) ?? false}
                      onChange={(_e, v) => setCampo('permitirPedidoSemLogin', v)}
                    />
                  }
                  label={
                    <Box sx={{ ml: 1.5 }}>
                      <Typography>Permitir pedido sem login</Typography>
                      <Typography variant="caption" color="text.secondary">
                        O cliente pode pedir sem entrar na conta, informando nome, telefone e endereço. Sem login, só
                        aparecem as formas de pagamento na entrega.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.freteGratisAPartirDe != null}
                      onChange={(_e, ligado) => setCampo('freteGratisAPartirDe', ligado ? '0.00' : null)}
                    />
                  }
                  label={
                    <Box sx={{ ml: 1.5 }}>
                      <Typography>Frete grátis automático</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pedidos de entrega a partir do valor abaixo não pagam o frete. A taxa de conveniência continua.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
                {form.freteGratisAPartirDe != null && (
                  <CampoValorReais
                    label="Frete grátis a partir de"
                    valor={form.freteGratisAPartirDe as number | undefined}
                    onChange={(v) => setCampo('freteGratisAPartirDe', v)}
                    ajuda="Deixe 0 para o frete ser sempre grátis."
                  />
                )}
              </Stack>
            </Secao>

            <SecaoAvisosCliente
              valor={form.emailAcompanhamentoPedido as boolean | undefined}
              onChange={(ligado) => setCampo('emailAcompanhamentoPedido', ligado)}
            />
          </GateModo>
        )}

        {/* ===================== ABA: COMANDA/MESA ===================== */}
        {aba === ABA.comandaMesa && (
          <GateModo modo="comandaMesa" modos={modos}>
            <InfoModo
              itens={[
                <>
                  Os pedidos caem no <strong>PDV de comanda/mesa</strong> do Sischef, na comanda ou mesa
                  informada — somam à conta em aberto, como um pedido tirado pelo garçom.
                </>,
                <>
                  O cliente lê o QR com o <strong>próprio celular</strong>. Não há pagamento no cardápio: a
                  conta é acertada com o garçom.
                </>,
                <>
                  Qual identificador manda na conta (mesa ou comanda) vem do{' '}
                  <strong>identificador principal</strong> configurado abaixo.
                </>,
              ]}
            />

            <Secao
              icone={<QrCode2Icon />}
              titulo="Pontos de acesso"
              descricao="Mesas e comandas configuradas. Gere os QR codes e gerencie na tela de acessos."
              acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['MESA', 'COMANDA']} />}
            >
              <PontosDeAcessoCard idConfig={idConfig} tipos={['MESA', 'COMANDA']} limite={3} />
            </Secao>

            <Secao
              icone={<TableRestaurantIcon />}
              titulo="Identificadores locais"
              descricao="Como mesa e comanda funcionam no salão da sua loja."
            >
              <Grid container spacing={3} alignItems="flex-start">
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select
                    label="Identificador principal"
                    fullWidth
                    value={(form.tipoIdentificadorPrincipal as string) ?? ''}
                    onChange={(e) => setCampo('tipoIdentificadorPrincipal', e.target.value || null)}
                    helperText="O QR-code da loja referencia este tipo"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    <MenuItem value="MESA">Mesa</MenuItem>
                    <MenuItem value="COMANDA">Comanda</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select
                    label="Identificador secundário"
                    fullWidth
                    value={(form.tipoIdentificadorSecundario as string) ?? 'NENHUM'}
                    onChange={(e) => setCampo('tipoIdentificadorSecundario', e.target.value)}
                    helperText="Solicitado em formulário no app"
                  >
                    <MenuItem value="NENHUM">Não usar</MenuItem>
                    <MenuItem value="MESA">Mesa</MenuItem>
                    <MenuItem value="COMANDA">Comanda</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={(form.identificadorSecundarioObrigatorio as boolean) ?? false}
                        onChange={(_e, v) => setCampo('identificadorSecundarioObrigatorio', v)}
                        disabled={!form.tipoIdentificadorSecundario || form.tipoIdentificadorSecundario === 'NENHUM'}
                      />
                    }
                    label={
                      <Box sx={{ ml: 1.5 }}>
                        <Typography>Secundário obrigatório</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Exige preenchimento antes de finalizar
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0, mt: { xs: 0, md: 1 } }}
                  />
                </Grid>
              </Grid>
            </Secao>

            <Secao
              icone={<RoomServiceIcon />}
              titulo="Chamar garçom"
              descricao="Opções que aparecem ao tocar em 'Chamar garçom' e onde o chamado aparece (impressora ou KDS). Mesa/comanda e tablet compartilham essa configuração."
            >
              <TextField
                label="Opções (uma por linha)"
                multiline
                minRows={4}
                fullWidth
                value={((form.tabletChamarGarcomOpcoes as string[] | null) ?? []).join('\n')}
                onChange={(e) => setCampo('tabletChamarGarcomOpcoes', e.target.value.split('\n'))}
                helperText="Até 8 opções. Em branco usa o padrão: Fechar a conta, Pedir a conta, Trazer água, Falar com o garçom."
              />
              <TextField
                select
                label="Onde o chamado aparece"
                fullWidth
                value={localImpressaoSalvo}
                onChange={(e) => setCampo('tabletLocalImpressao', e.target.value || null)}
                sx={{ mt: 3, maxWidth: 360 }}
                helperText={textoAjudaDestino(localImpressaoSalvo, opcoesLocalImpressao)}
              >
                <MenuItem value="">Não configurado</MenuItem>
                <MenuItem value={DESTINO_CHAMADO_KDS}>KDS (tela da cozinha)</MenuItem>
                {todasOpcoesLocal.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Secao>

            <Secao icone={<PaidIcon />} titulo="Pedido mínimo" descricao="Valor mínimo para pedidos de mesa/comanda.">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CampoValorReais
                    label="Pedido mínimo (mesa/comanda)"
                    valor={form.valorMinimoMesaComanda as number | undefined}
                    onChange={(v) => setCampo('valorMinimoMesaComanda', v)}
                    ajuda="Deixe 0 para não exigir valor mínimo."
                  />
                </Grid>
              </Grid>
            </Secao>
          </GateModo>
        )}

        {/* ===================== ABA: TOTEM ===================== */}
        {aba === ABA.totem && (
          <SecaoTotemPedidos
            modos={modos}
            form={form}
            idConfig={idConfig}
            pontos={pontosTotem}
            idPontoSelecionado={idPontoTotem}
            onSelecionarPonto={setIdPontoTotem}
            overridesPontos={overridesPontos}
            setOverridePonto={setOverridePonto}
          />
        )}

        {/* ===================== ABA: VITRINE ===================== */}
        {aba === ABA.vitrine && (
          <GateModo modo="vitrine" modos={modos}>
            <InfoModo
              itens={[
                <>
                  A vitrine <strong>não gera pedido</strong>: nada chega ao Sischef. É só exibição do cardápio,
                  para divulgar preços e fotos.
                </>,
                <>
                  Não há carrinho nem checkout — o cliente navega e vê os produtos, e mais nada.
                </>,
              ]}
            />

            <Secao
              icone={<QrCode2Icon />}
              titulo="Pontos de acesso"
              descricao="Endereços do cardápio em modo vitrine (somente exibição — o cliente vê os produtos, mas não faz pedido)."
              acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['VITRINE']} />}
            >
              <PontosDeAcessoCard idConfig={idConfig} tipos={['VITRINE']} />
            </Secao>
          </GateModo>
        )}

        {/* ===================== ABA: TABLET (NA MESA) ===================== */}
        {aba === ABA.tablet && (
          <GateModo modo="tablet" modos={modos}>
            <InfoModo
              itens={[
                <>
                  Os pedidos caem no <strong>PDV de comanda/mesa</strong> do Sischef, no mesmo lugar dos
                  pedidos feitos pelo QR de mesa/comanda.
                </>,
                <>
                  <strong>O número da mesa é informado no próprio tablet</strong>, não aqui: o link identifica
                  a loja, não a mesa. Abra o link no aparelho e toque em <strong>Sobre</strong> (canto inferior
                  esquerdo) → <strong>Configurações do aparelho</strong>, informe o{' '}
                  <strong>PIN do dispositivo</strong> (aba <strong>Gerais</strong>) e defina a mesa. Cada tablet
                  guarda a sua, e sem ela o aparelho não deixa pedir.
                </>,
                <>
                  O tablet fica <strong>fixo na mesa</strong> e é usado por vários clientes: não há login nem
                  pagamento, e a conta é acertada com o garçom.
                </>,
              ]}
            />

            <Secao
              icone={<QrCode2Icon />}
              titulo="Pontos de acesso"
              descricao="Tablets fixos na mesa. Use 'Configurar tablet' para o passo a passo de deixar o aparelho travado no cardápio (kiosk)."
              acoes={<BotaoGerenciarPontosDeAcesso idConfig={idConfig} tipos={['TABLET']} />}
            >
              <PontosDeAcessoCard idConfig={idConfig} tipos={['TABLET']} guiaTablet />
            </Secao>

            <Secao
              icone={<SlideshowIcon />}
              titulo="Imagens da mesa"
              descricao="Proteção de tela do tablet, quando a mesa fica parada."
            >
              <UploadImagensTotem
                valor={form.imagensTablet as string[] | null}
                onChange={(v) => setCampo('imagensTablet', v)}
                orientacao="paisagem"
                ajuda="Até 5 imagens que passam no fundo da proteção de tela do tablet (ex.: pratos, promoções). Sem imagens configuradas, entra a tela da marca com o convite a tocar. O tablet fica preso na mesa DEITADO, então as imagens são na horizontal — ao contrário do totem, que fica em pé."
              />
            </Secao>

            <Secao
              icone={<TabletAndroidIcon sx={{ transform: 'rotate(90deg)' }} />}
              titulo="Modo do tablet"
              descricao="Como o tablet identifica o pedido."
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.tabletIdentificador === 'COMANDA'}
                    onChange={(_e, v) => setCampo('tabletIdentificador', v ? 'COMANDA' : 'MESA')}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography>Solicitar número da comanda?</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Marcado: o cliente informa a comanda no checkout (identificador principal). Desmarcado: usa
                      o número da mesa configurado no próprio tablet.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />

              {/* Só faz sentido com comanda: no modo mesa o número vem do aparelho. */}
              {form.tabletIdentificador === 'COMANDA' && (
                <TextField
                  select
                  label="Como o cliente informa a comanda"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={(form.tabletEntradaComanda as string) ?? 'DIGITAR'}
                  onChange={(e) => setCampo('tabletEntradaComanda', e.target.value)}
                  helperText="Lendo o QR Code: a câmera abre sozinha e NÃO há campo para digitar — se ela falhar, a tela manda chamar o garçom. Os dois: o cliente escolhe. Ler QR exige as etiquetas de comanda impressas."
                >
                  <MenuItem value="DIGITAR">Digitando o número</MenuItem>
                  <MenuItem value="QRCODE">Só lendo o QR Code da comanda</MenuItem>
                  <MenuItem value="AMBOS">Os dois (QR Code em destaque)</MenuItem>
                </TextField>
              )}

              {/* Escolher "ler QR" sem ter as etiquetas impressas entrega uma
                  câmera que não lê nada — e as comandas já existem no Sischef,
                  não faz sentido recadastrar uma a uma no cardápio. */}
              {form.tabletIdentificador === 'COMANDA' &&
                ((form.tabletEntradaComanda as string) ?? 'DIGITAR') !== 'DIGITAR' && (
                  <EtiquetasDaComanda idConfig={idConfig} />
                )}
            </Secao>

            <Secao
              icone={<RoomServiceIcon />}
              titulo="Chamar garçom"
              descricao="Opções que aparecem ao tocar em 'Chamar garçom' e onde o chamado aparece (impressora ou KDS). Compartilhadas com o QR de mesa/comanda."
            >
              <TextField
                label="Opções (uma por linha)"
                multiline
                minRows={4}
                fullWidth
                value={((form.tabletChamarGarcomOpcoes as string[] | null) ?? []).join('\n')}
                onChange={(e) => setCampo('tabletChamarGarcomOpcoes', e.target.value.split('\n'))}
                helperText="Até 8 opções. Em branco usa o padrão: Fechar a conta, Pedir a conta, Trazer água, Falar com o garçom."
              />
              <TextField
                select
                label="Onde o chamado aparece"
                fullWidth
                value={localImpressaoSalvo}
                onChange={(e) => setCampo('tabletLocalImpressao', e.target.value || null)}
                sx={{ mt: 3, maxWidth: 360 }}
                helperText={textoAjudaDestino(localImpressaoSalvo, opcoesLocalImpressao)}
              >
                <MenuItem value="">Não configurado</MenuItem>
                <MenuItem value={DESTINO_CHAMADO_KDS}>KDS (tela da cozinha)</MenuItem>
                {todasOpcoesLocal.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Secao>

            <AvisoPinDispositivo comoAbrir />
          </GateModo>
        )}

        {/* ===================== ABA: TOTEM CHECKOUT ===================== */}
        {aba === ABA.totemCheckout && (
          <SecaoCheckoutKiosk
            modos={modos}
            form={form}
            idConfig={idConfig}
            pontos={pontosCheckout}
            idPontoSelecionado={idPontoCheckout}
            onSelecionarPonto={setIdPontoCheckout}
            overridesPontos={overridesPontos}
            setOverridePonto={setOverridePonto}
          />
        )}
      </Stack>

      {/* Barra fixa no rodapé, igual à da tela de Visibilidade dos itens. A tela
          é longa e tem várias abas; com o botão no fim do conteúdo, salvar exigia
          rolar até o fim toda vez. O alerta de falha vem junto na barra para
          aparecer onde a ação está, e não fora da vista. */}
      <Paper
        elevation={0}
        sx={(t) => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          mt: 4,
          p: 1.5,
          borderRadius: 1,
          borderTop: 1,
          borderColor: 'divider',
          // Sombra só para CIMA: a barra fica colada no rodapé, então é na borda
          // de cima que o conteúdo passa por baixo e precisa de separação. Uma
          // sombra para baixo ficaria fora da tela, e a caixa toda sombreada
          // (elevation) fazia a barra parecer um cartão flutuante.
          boxShadow: t.palette.mode === 'dark' ? '0 -6px 14px rgba(0,0,0,0.45)' : '0 -6px 14px rgba(0,0,0,0.06)',
        })}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          {statusAtualizar.error || erroSalvar ? (
            <Alert severity="error" sx={{ py: 0, flexGrow: 1 }}>
              {erroSalvar ?? 'Falha ao salvar'}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              O formulário é único: salvar grava as alterações de todas as abas.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0 }}>
            {replica?.idOrigem != null && replica.pontos.length > 1 && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContentCopyIcon />}
                onClick={() => setConfirmandoReplica(true)}
                disabled={statusAtualizar.isLoading}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Salvar e replicar para outros totens
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={() => void salvar()}
              disabled={statusAtualizar.isLoading}
              sx={{ minWidth: { sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
            >
              {statusAtualizar.isLoading ? <CircularProgress size={22} /> : 'Salvar alterações'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {replica?.idOrigem != null && (
        <DialogReplicarConfigTotem
          aberto={confirmandoReplica}
          onFechar={() => setConfirmandoReplica(false)}
          origem={replica.pontos.find((p) => p.id === replica.idOrigem) ?? null}
          destinos={replica.pontos.filter((p) => p.id !== replica.idOrigem)}
          onConfirmar={async (idsDestino) => {
            setConfirmandoReplica(false);
            await salvar(idsDestino);
          }}
        />
      )}

      <Snackbar
        open={salvo}
        autoHideDuration={3000}
        onClose={() => setSalvo(false)}
        message="Configuração salva"
      />

      <Snackbar
        open={replicados !== null}
        autoHideDuration={4000}
        onClose={() => setReplicados(null)}
        message={`Configuração replicada para ${replicados} ${replicados === 1 ? 'totem' : 'totens'}`}
      />
    </>
  );
}

// ===== Opção de impressão do totem =====

export function OpcaoImpressaoTotem({
  marcada,
  aoMudar,
  titulo,
  ajuda,
}: {
  marcada: boolean;
  aoMudar: (marcada: boolean) => void;
  titulo: string;
  ajuda: string;
}): JSX.Element {
  return (
    <FormControlLabel
      control={<Checkbox checked={marcada} onChange={(_e, v) => aoMudar(v)} />}
      sx={{ alignItems: 'flex-start', m: 0 }}
      label={
        <Box sx={{ pt: 0.75 }}>
          <Typography>{titulo}</Typography>
          <Typography variant="body2" color="text.secondary">
            {ajuda}
          </Typography>
        </Box>
      }
    />
  );
}

// ===== Secao =====

interface SecaoProps {
  icone: ReactNode;
  titulo: string;
  descricao: string;
  /** Ação secundária da seção, alinhada à direita do cabeçalho. */
  acoes?: ReactNode;
  children: ReactNode;
}

export function Secao({ icone, titulo, descricao, acoes, children }: SecaoProps): JSX.Element {
  const tema = useTheme();
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: tema.palette.mode === 'dark' ? 'rgba(241,11,92,0.15)' : 'rgba(241,11,92,0.08)',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            {icone}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {titulo}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {descricao}
            </Typography>
          </Box>
        </Stack>
        {acoes}
      </Stack>
      {children}
    </Paper>
  );
}

/**
 * Ponteiro para o PIN, que fica em GERAIS por ser o mesmo para totem e tablet —
 * uma loja que contratou só um dos dois modos não enxerga a aba do outro.
 */
// ===== Campo de valor em R$ (pedido mínimo por canal) =====

export function CampoValorReais({
  label,
  valor,
  onChange,
  ajuda,
}: {
  label: string;
  valor: number | undefined;
  onChange: (v: number) => void;
  ajuda?: string;
}): JSX.Element {
  return (
    <TextField
      label={label}
      fullWidth
      type="number"
      value={valor ?? ''}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      helperText={ajuda}
      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
    />
  );
}

// ===== Sliders =====

interface SliderTempoProps {
  rotulo: string;
  ajuda: string;
  min: number;
  max: number;
  step: number;
  marks: Array<{ value: number; label: string }>;
  valor: number;
  onChange: (v: number) => void;
}

function SliderTempo({ rotulo, ajuda, min, max, step, marks, valor, onChange }: SliderTempoProps): JSX.Element {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography>{rotulo}</Typography>
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
          {valor} min
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {ajuda}
      </Typography>
      <Box sx={{ px: { xs: 2, sm: 1 } }}>
        <Slider
          value={valor}
          min={min}
          max={max}
          step={step}
          marks={marks}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} min`}
          onChange={(_e, v) => onChange(Array.isArray(v) ? v[0]! : v)}
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );
}

interface SliderRangeProps {
  rotulo: string;
  ajuda: string;
  min: number;
  max: number;
  step: number;
  marks: Array<{ value: number; label: string }>;
  valorMin: number;
  valorMax: number;
  onChange: (min: number, max: number) => void;
}

function SliderTempoRange({
  rotulo,
  ajuda,
  min,
  max,
  step,
  marks,
  valorMin,
  valorMax,
  onChange,
}: SliderRangeProps): JSX.Element {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography>{rotulo}</Typography>
        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
          {valorMin}–{valorMax} min
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {ajuda}
      </Typography>
      <Box sx={{ px: { xs: 2, sm: 1 } }}>
        <Slider
          value={[valorMin, valorMax]}
          min={min}
          max={max}
          step={step}
          marks={marks}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v} min`}
          onChange={(_e, v) => {
            if (Array.isArray(v)) onChange(v[0]!, v[1]!);
          }}
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );
}
